import Tooltip from "rc-tooltip";
import { EAccessLevel } from "../../../../shared/oversight-core/enums/access-level";
import { EAccessScope } from "../../../../shared/oversight-core/enums/access-scope";
import { EOrgUserStatus } from "../../../../shared/oversight-core/enums/org-user-status";
import { IAccessGrant } from "../../../../shared/oversight-core/interfaces/access-grant";
import {
  getGrantDescription,
  isGrantExpired,
  sortGrantsByStrength,
} from "../../../../shared/oversight-core/utils/get-org-user-info";
import AccessBadge from "../access-badge/access-badge";
import styles from "./user-access-cell.module.scss";

interface IProps {
  grants: IAccessGrant[];
  status: EOrgUserStatus;
}

const noAccessGrant: IAccessGrant = {
  identity: "",
  level: EAccessLevel.NO_ACCESS,
  scope: EAccessScope.ORGANIZATION,
  scopeName: "",
  path: [],
  ancestorIds: [],
};

const isGrantInEffect = (grant: IAccessGrant) =>
  !isGrantExpired(grant) && !grant.isMissing;

// "3 sites", "2 branches", or "3 grants" when the scopes are mixed.
const countScopes = (grants: IAccessGrant[]) => {
  const scope = grants[0].scope;
  const count = grants.length;

  if (grants.some((grant) => grant.scope !== scope)) {
    return `${count} grants`;
  }

  switch (scope) {
    case EAccessScope.SITE:
      return `${count} sites`;
    case EAccessScope.BRANCH:
      return `${count} branches`;
    default:
      return `${count} organizations`;
  }
};

const GrantLine = (props: { grant: IAccessGrant; scopeText?: string }) => {
  const { grant, scopeText } = props;

  return (
    <div className="d-flex align-items-center gap-2">
      <AccessBadge level={grant.level} />
      {grant.level !== EAccessLevel.NO_ACCESS && (
        <span className="text-dark font-size-13 font-weight-500 text-nowrap">
          {scopeText ?? grant.scopeName}
        </span>
      )}
    </div>
  );
};

const UserAccessCell = (props: IProps) => {
  // The pill shows the strongest active grant, or No access when the user has none.
  const grants =
    props.status === EOrgUserStatus.NO_ACCESS || props.grants.length === 0
      ? [noAccessGrant]
      : sortGrantsByStrength(props.grants);
  const [primaryGrant] = grants;

  // Grants in effect at the strongest level are summed up on the first line ("Operate 3 sites"),
  // so the reach is readable without hovering; the rest still sit behind the hover.
  const primaryGroup = isGrantInEffect(primaryGrant)
    ? grants.filter(
        (grant) => grant.level === primaryGrant.level && isGrantInEffect(grant),
      )
    : [primaryGrant];
  const otherGrantCount = grants.length - primaryGroup.length;

  const allGrantsOverlay = (
    <div className="d-flex flex-column gap-3 p-1">
      {grants.map((grant, index) => (
        <div key={index}>
          <GrantLine grant={grant} />
          <div className="text-light font-size-11 mt-1">
            {getGrantDescription(grant)}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <GrantLine
        grant={primaryGrant}
        scopeText={
          primaryGroup.length > 1 ? countScopes(primaryGroup) : undefined
        }
      />
      {grants.length > 1 ? (
        <Tooltip
          placement="bottom"
          trigger={["hover"]}
          overlay={allGrantsOverlay}
        >
          <span
            className={`${styles.moreGrants} text-light font-size-11 cursor-pointer`}
          >
            {otherGrantCount > 0
              ? `+ ${otherGrantCount} more grant${otherGrantCount > 1 ? "s" : ""}`
              : "View grants"}
          </span>
        </Tooltip>
      ) : (
        <div className="text-light font-size-11 mt-1">
          {getGrantDescription(primaryGrant)}
        </div>
      )}
    </div>
  );
};

export default UserAccessCell;
